import { h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

/**
 * Product-detail preview: the live monogram overlaid on the current variant
 * image, used when the monogram component has `enableProductDetail` set and the
 * variant has an image (see <MonogramContent />).
 *
 * Because a contained image is letterboxed inside its element, positioning the
 * overlay against the element box would drift for any aspect ratio that doesn't
 * match the stage. Instead we compute the rectangle the image actually occupies
 * and place the monogram in percentages of that box — using the placement's
 * `productDetailTop` / `productDetailLeft` (to the monogram's centre) and
 * `productDetailScale` (its width) — so alignment holds for any aspect ratio.
 */
export default function MonogramProductDetail({ image, placement, children }) {
  const stageRef = useRef();

  // The storefront API always returns the image dimensions, so the aspect ratio
  // (and thus the overlay) is correct on the first render with no wait for the
  // image to load.
  const aspect =
    image?.width && image?.height ? image.width / image.height : null;

  const [box, setBox] = useState(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !aspect) return undefined;

    const measure = () => {
      const cw = stage.clientWidth;
      const ch = stage.clientHeight;
      if (!cw || !ch) return;
      // Fit the image into the stage preserving aspect (contain).
      let width = cw;
      let height = cw / aspect;
      if (height > ch) {
        height = ch;
        width = ch * aspect;
      }
      setBox({
        width,
        height,
        left: (cw - width) / 2,
        top: (ch - height) / 2,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [aspect]);

  // Before the image rect is known, the box fills the stage so the image still
  // shows (contained) while we measure; the monogram is held back until then.
  const boxStyle = box
    ? {
        width: `${box.width}px`,
        height: `${box.height}px`,
        left: `${box.left}px`,
        top: `${box.top}px`,
      }
    : { inset: 0 };

  const monogramStyle = {
    left: `${num(placement?.productDetailLeft, 50)}%`,
    top: `${num(placement?.productDetailTop, 50)}%`,
    width: `${num(placement?.productDetailScale, 50)}%`,
  };

  return (
    <div className="monogram-preview-detail" ref={stageRef}>
      <div className="monogram-preview-detail__image-box" style={boxStyle}>
        <img
          className="monogram-preview-detail__image"
          src={image?.url}
          alt=""
        />
        {box && (
          <div
            className="monogram-preview-detail__monogram"
            style={monogramStyle}
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

function num(value, fallback) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}
